import type Phaser from 'phaser';
import { ONLINE_ENABLED } from '../../platform/online';
import { gameAudio } from '../audio/audio';
import { HEADER_H } from './navChrome';
import { label, UI } from './text';

/**
 * The CLUB tab (docs/ONLINE_UI.md): where everything with other players lives, in sections picked
 * along the top. It replaces the TROPHY tab in builds with online play (YOU is the trophy shelf);
 * without it the tab stays TROPHY, with no sections.
 */

export type ClubSection = 'you' | 'versus' | 'boards' | 'made';

/** The section bar under the header. */
export const CLUB_BAR_H = 16;

const SECTIONS: { id: ClubSection; name: string; scene: string }[] = [
  { id: 'you', name: 'YOU', scene: 'Trophy' },
  { id: 'versus', name: 'VERSUS', scene: 'Versus' },
  { id: 'boards', name: 'BOARDS', scene: 'Boards' },
  { id: 'made', name: 'MADE', scene: 'Community' },
];

/** Whether the fifth tab is CLUB (online play built in) rather than TROPHY. */
export function clubOn(): boolean {
  return ONLINE_ENABLED;
}

/**
 * The section bar: each section's name, the current one bright and underlined. Switching is
 * instant, like the tabs; tapping the current one starts it over (from a lawn's card back to the
 * list). Returns the y where the section's content starts.
 */
export function drawClubBar(scene: Phaser.Scene, active: ClubSection): number {
  const { width } = scene.scale.gameSize;
  const top = HEADER_H + 2;
  const g = scene.add.graphics();
  g.fillStyle(UI.panel).fillRect(0, top, width, CLUB_BAR_H);
  g.fillStyle(UI.rule).fillRect(0, top + CLUB_BAR_H, width, 1);
  g.fillStyle(UI.shadow).fillRect(0, top + CLUB_BAR_H + 1, width, 1);
  const w = width / SECTIONS.length;
  SECTIONS.forEach(({ id, name, scene: key }, i) => {
    const x = Math.round(i * w);
    const cw = Math.round((i + 1) * w) - x;
    const on = id === active;
    const text = label(scene, x + cw / 2, top + 4, name, 1, on ? UI.accent : UI.dim).setOrigin(
      0.5,
      0,
    );
    if (on) {
      g.fillStyle(UI.accent).fillRect(
        Math.round(text.x - text.width / 2) - 2,
        top + CLUB_BAR_H - 3,
        text.width + 4,
        1,
      );
    }
    scene.add
      .zone(x, top, cw, CLUB_BAR_H)
      .setOrigin(0)
      .setData('label', name)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => {
        gameAudio.play('click');
        scene.scene.start(key);
      });
  });
  return top + CLUB_BAR_H + 2;
}
